import './feedPostCard.scss';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  Copy,
  EyeOff,
  Flag,
  Map as MapIcon,
  MessagesSquare,
  MoreHorizontal,
  Share,
  ShieldBan,
  ThumbsDown,
  ThumbsUp,
  UserX,
  VolumeX,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import CardComponent from '../../components/UI/Card/Card.component';
import {
  BookmarkIcon,
  CommentIcon,
  HeartIcon,
} from '../../components/UserFeed/FeedIcons';
import ErrorMessages from '../../constants/error-messages';
import { postAnalysisPath } from '../../helpers/postAnalysisPath';
import { postSentimentPath } from '../../helpers/postSentimentPath';
import { formatScore } from '../../helpers/scoreDisplay';
import type { FeedAuthor, FeedPost } from '../../interfaces/feed.interface';
import type { ScoreUpdateData } from '../../interfaces/websocket.interface';

export interface FeedPostCardProps {
  post: FeedPost;
  userId: string;
  username?: string;
  onToggleLike: (post: FeedPost) => void;
  onToggleBookmark: (post: FeedPost) => void;
  onReply: (postId: string) => void;
  /** TRIL-583 — go and READ the replies; distinct from onReply, which writes one. */
  onViewReplies: (postId: string) => void;
  onCopyLink: (postId: string) => void;
  onOpenShare: (postId: string, postTitle: string, post: FeedPost) => void;
  onMuteThread: (post: FeedPost) => void;
  onMuteAccount: (authorId: string | null) => void;
  onBlockAccount: (authorId: string | null) => void;
  onOpenReport: (postId: string) => void;
  onShowMoreLikeThis?: (postId: string) => void;
  onShowLessLikeThis?: (postId: string) => void;
  onHidePost?: (postId: string) => void;
  scoreUpdate?: ScoreUpdateData | null;
  showScore?: boolean;
  showCategories?: boolean;
}

// Pure preview/reading-time helpers — moved verbatim from recommendationFeed
// so the card stays presentational (no closures over feed state).
const getPostPreview = (post: FeedPost): string => {
  if (!post.textBlocks || post.textBlocks.length === 0) {
    return `${ErrorMessages.FEED.PREVIEW}`;
  }

  const textBlock = post.textBlocks.find(
    (block) => block.type === 'Paragraph' && block.data?.paragraph,
  );

  if (textBlock?.data?.paragraph) {
    // Strip markdown links [text](url) → text
    const paragraph = textBlock.data.paragraph
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .trim();
    return paragraph.length > 150 ? paragraph.slice(0, 140) + '…' : paragraph;
  }

  return `${ErrorMessages.FEED.PREVIEW}`;
};

const getReadingTime = (post: FeedPost): number => {
  if (!post.textBlocks || post.textBlocks.length === 0) return 1;
  const textContent = post.textBlocks
    .filter((block) => block.type === 'Paragraph')
    .map((block) => block.data?.paragraph || '')
    .join(' ');

  const wordCount = textContent.split(' ').length;
  return Math.max(1, Math.ceil(wordCount / 200));
};

export const FeedPostCard = (props: FeedPostCardProps) => {
  const {
    post,
    userId,
    username,
    onToggleLike,
    onToggleBookmark,
    onReply,
    onViewReplies,
    onCopyLink,
    onOpenShare,
    onMuteThread,
    onMuteAccount,
    onBlockAccount,
    onOpenReport,
    onShowMoreLikeThis,
    onShowLessLikeThis,
    onHidePost,
    scoreUpdate,
    showScore,
    showCategories,
  } = props;

  const postId = post.postId ?? post.id;
  // Record where the analysis link was opened from so the analysis page's Back
  // returns here (the feed) rather than the post detail page (TRIL-288).
  const location = useLocation();

  // Show the post's persisted score by default; live WS update overrides if
  // one has arrived. Earlier the badge was hidden until a STORY_SCORE_UPDATED
  // event landed, which left feed cards scoreless until the next reanalysis.
  const persistedScore = (post as any).score;
  const displayScore =
    scoreUpdate?.newScore ??
    (typeof persistedScore === 'number' ? persistedScore : null);

  const headerBlock = post.textBlocks?.[0];
  const postTitle =
    headerBlock?.type === 'Heading' && headerBlock.data?.header
      ? headerBlock.data.header
      : 'No Title';

  const authorId =
    (post.author as FeedAuthor)?.authorId ??
    (post.author as FeedAuthor)?.userId ??
    post.userId ??
    null;

  const isOwnPost =
    (!!authorId && authorId === userId) || post.author?.username === username;

  // TRIL-534 — name the account the action affects, matching mobile's PostOverflowMenu.
  // The fallback is load-bearing: the destructive labels must still read as a sentence
  // when the username is missing, rather than "Block @" or a bare "Block".
  const authorHandle = post.author?.username
    ? `@${post.author.username}`
    : 'this account';

  const hasLiked = !!post.viewerInteractions?.hasLiked;
  const hasBookmarked = !!post.viewerInteractions?.hasBookmarked;

  const likeCount = post.viewerInteractions?.likeCount ?? 0;
  const replyCount = post.replyCount ?? 0;
  const bookmarkCount = post.viewerInteractions?.bookmarkCount ?? 0;

  return (
    <CardComponent
      post={post}
      cardMinWidth={300}
      raised={false}
      className={'USER__FEED__ITEM'}
      userId={userId}
    >
      <div className="feed-card__header">
        <h3 className="ITEM_TITLE">{postTitle}</h3>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              className="feed-card__ellipsis"
              aria-label="Post actions"
            >
              <MoreHorizontal size={18} />
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className="feed-card__menu"
              sideOffset={4}
              align="end"
            >
              <DropdownMenu.Item
                className="feed-card__menu-item"
                onClick={() => onCopyLink(postId)}
              >
                <Copy size={16} />
                <span>Copy link</span>
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className="feed-card__menu-item"
                onClick={() => onOpenShare(postId, postTitle, post)}
              >
                <Share size={16} />
                <span>Share post via...</span>
              </DropdownMenu.Item>

              <DropdownMenu.Separator className="feed-card__menu-separator" />

              {onShowMoreLikeThis && (
                <DropdownMenu.Item
                  className="feed-card__menu-item"
                  onClick={() => onShowMoreLikeThis(postId)}
                >
                  <ThumbsUp size={16} />
                  <span>Show more like this</span>
                </DropdownMenu.Item>
              )}
              {onShowLessLikeThis && (
                <DropdownMenu.Item
                  className="feed-card__menu-item"
                  onClick={() => onShowLessLikeThis(postId)}
                >
                  <ThumbsDown size={16} />
                  <span>Show less like this</span>
                </DropdownMenu.Item>
              )}

              {/* TRIL-260: self-targeting actions hidden on your own content */}
              {!isOwnPost && (
                <>
                  <DropdownMenu.Separator className="feed-card__menu-separator" />

                  <DropdownMenu.Item
                    className="feed-card__menu-item"
                    onClick={() => onMuteThread(post)}
                  >
                    <VolumeX size={16} />
                    <span>Mute thread</span>
                  </DropdownMenu.Item>
                  {onHidePost && (
                    <DropdownMenu.Item
                      className="feed-card__menu-item"
                      onClick={() => onHidePost(postId)}
                    >
                      <EyeOff size={16} />
                      <span>Hide post for me</span>
                    </DropdownMenu.Item>
                  )}
                  <DropdownMenu.Item
                    className="feed-card__menu-item"
                    onClick={() => onMuteAccount(authorId)}
                  >
                    <UserX size={16} />
                    <span>Mute {authorHandle}</span>
                  </DropdownMenu.Item>

                  <DropdownMenu.Separator className="feed-card__menu-separator" />

                  <DropdownMenu.Item
                    className="feed-card__menu-item feed-card__menu-item--danger"
                    onClick={() => onBlockAccount(authorId)}
                  >
                    <ShieldBan size={16} />
                    <span>Block {authorHandle}</span>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    className="feed-card__menu-item feed-card__menu-item--danger"
                    onClick={() => onOpenReport(postId)}
                  >
                    <Flag size={16} />
                    <span>Report post</span>
                  </DropdownMenu.Item>
                </>
              )}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>

      <p className="post-preview">
        {getPostPreview(post)}{' '}
        {postId && (
          <Link className="read-more-inline" to={`/post/${postId}`}>
            Read More
          </Link>
        )}
        <span className="reading-time">
          {' · '}
          {getReadingTime(post)} min read
        </span>
      </p>

      {showScore && (
        <Link
          to={postAnalysisPath(postId)}
          state={{ from: location.pathname + location.search }}
          className={`score-badge ${scoreUpdate ? 'score-updated' : ''}`}
          title="View full story analysis"
        >
          Score: {formatScore(displayScore)}
          {scoreUpdate && scoreUpdate.scoreDelta.change !== 0 && (
            <span
              className={`score-change score-${scoreUpdate.scoreDelta.change_direction}`}
            >
              {' '}
              {scoreUpdate.scoreDelta.change > 0 ? '+' : ''}
              {scoreUpdate.scoreDelta.change.toFixed(1)}
            </span>
          )}
        </Link>
      )}

      {showCategories && post.categories && post.categories.length > 0 && (
        <div className="post-categories">
          {post.categories.map((category) => (
            <span key={category} className="category-tag">
              {category}
            </span>
          ))}
        </div>
      )}

      <div className="feed-card__actions">
        {/*
          TRIL-583 — two separate actions. The bubble WRITES a reply and carries no
          count; the count belongs to the action that goes and READS them. Previously
          one control did both, so tapping it to read 12 replies opened a blank editor.
        */}
        <button
          type="button"
          className="feed-card__action"
          aria-label="Reply"
          onClick={() => onReply(postId)}
        >
          <CommentIcon size={18} fill="currentColor" />
        </button>

        <button
          type="button"
          className="feed-card__action"
          aria-label={`View replies, ${replyCount} replies`}
          onClick={() => onViewReplies(postId)}
        >
          <MessagesSquare size={18} />
          <span className="feed-card__action-count">{replyCount}</span>
        </button>

        <button
          type="button"
          className="feed-card__action feed-card__action--share"
          aria-label="Share"
          onClick={() => onOpenShare(postId, postTitle, post)}
        >
          <Share size={18} />
        </button>

        <button
          type="button"
          className={`feed-card__action feed-card__action--like ${hasLiked ? 'feed-card__action--active' : ''}`}
          aria-label={`Like, ${likeCount} likes`}
          onClick={() => onToggleLike(post)}
        >
          <HeartIcon size={18} fill={hasLiked ? '#e0245e' : 'currentColor'} />
          <span className="feed-card__action-count">{likeCount}</span>
        </button>

        <button
          type="button"
          className={`feed-card__action feed-card__action--bookmark ${hasBookmarked ? 'feed-card__action--active' : ''}`}
          aria-label={`Bookmark, ${bookmarkCount} bookmarks`}
          onClick={() => onToggleBookmark(post)}
        >
          <BookmarkIcon
            size={18}
            fill={hasBookmarked ? '#1d9bf0' : 'currentColor'}
          />
          <span className="feed-card__action-count">{bookmarkCount}</span>
        </button>

        <Link
          to={postSentimentPath(postId)}
          state={{ from: location.pathname + location.search }}
          className="feed-card__action feed-card__action--sentiment"
          aria-label="Sentiment map"
          title="Sentiment map — where this story lands"
        >
          <MapIcon size={18} />
        </Link>
      </div>
    </CardComponent>
  );
};

export default FeedPostCard;
